/* eslint-disable max-lines */
import {Lang} from 'yougile-lang'
import {ArrUtil, relativeTimeToTimestamp, SearchUtil, Time} from 'yougile-util'
import {
  fixOldReportColumn,
  getChecklistsAsText,
  getRangeValue,
  LocationFilterItem,
  noBoardContainerId,
  Subtask
} from 'yougile-model'
import {DataObjectId} from './data-object'
import {
  ReportColumn,
  ReportDateFilter,
  ReportTextFilterPlace,
  ReportUserFilter
} from './report'
import {
  createdAtField,
  getAllFields,
  getStickersFromTasks,
  ReportDataField
} from './report-data-field'
import {
  assignedStickerStateToIds,
  DeadlineStickerEntry,
  DeadlineStickerValue,
  isSetSticker,
  timerToInterval
} from './sticker'
import {StickerData} from './sticker-data'
import {TaskData} from './task-data'
import {TaskDataService} from './task-data-service'

const userChecker = (filters: ReportUserFilter[]) => {
  return (userId: DataObjectId | undefined) =>
    filters.find(f => {
      const res = f.user === 'not-set' ? !userId : userId === f.user
      return f.negate ? !res : res
    })
}

const multiUserChecker = (filters: ReportUserFilter[]) => {
  return (users: DataObjectId[]) =>
    filters.find(f => {
      const res =
        f.user === 'not-set' ? users.length === 0 : users.includes(f.user)
      return f.negate ? !res : res
    })
}

const dateChecker = (filter: ReportDateFilter) => {
  const start = filter.start
    ? relativeTimeToTimestamp(filter.start, filter.datesFrom)
    : undefined
  const end = filter.end
    ? relativeTimeToTimestamp(filter.end, filter.datesFrom)
    : undefined
  return (timestamp: number | undefined) => {
    if (filter.type === 'expired') {
      return timestamp && Time.now() > timestamp
    }
    if (filter.type === 'not-expired') {
      return timestamp && Time.now() <= timestamp
    }
    return (
      (!start || (timestamp && start <= timestamp)) &&
      (!end || (timestamp && end > timestamp))
    )
  }
}

const dateCheckerBetween = (filter: ReportDateFilter) => {
  const filterStart = filter.start
    ? relativeTimeToTimestamp(filter.start, filter.datesFrom)
    : undefined
  const filterEnd = filter.end
    ? relativeTimeToTimestamp(filter.end, filter.datesFrom)
    : undefined
  return (from: number, to: number) => {
    if (filter.type === 'expired') {
      return Time.now() > from
    }
    if (filter.type === 'not-expired') {
      return Time.now() <= from
    }

    return (
      ((!filterStart || filterStart <= from) &&
        (!filterEnd || to < filterEnd)) ||
      ((!filterStart || filterStart <= from) &&
        (!filterEnd || from < filterEnd)) ||
      ((!filterStart || filterStart <= to) && (!filterEnd || to < filterEnd)) ||
      ((!filterStart || from <= filterStart) && (!filterEnd || filterEnd < to))
    )
  }
}

// eslint-disable-next-line max-lines-per-function
export const getReportTasks = (
  column: ReportColumn,
  allTasks: TaskData[],
  allStickers: StickerData[],
  taskDataService: TaskDataService
): TaskData[] => {
  if (!column?.filter || !allTasks) {
    return []
  }
  const {
    createdBy,
    assignedTo,
    assignedBy,
    responsible,
    createdAt,
    movedAt,
    lastMessageAt,
    deadline,
    completedAt,
    located,
    isArchived,
    isCompleted,
    stickers,
    timetracking,
    timer,
    stopwatch,
    subtasks,
    subtasksCompleted,
    subtasksAssigned,
    subtasksDeadline,
    text,
    chat
  } = fixOldReportColumn(column).filter

  let result: TaskData[] = allTasks.filter(t => !!t)
  const stickerIds = taskDataService.getStickerIds()

  if (located && located.length > 0) {
    const check = (task: TaskData, filter: LocationFilterItem): boolean => {
      if (
        (!!filter.subtasks.level1 && task.locations1.includes(filter.parent)) ||
        (!!filter.subtasks.other && task.locationsOther.includes(filter.parent))
      ) {
        return true
      }
      if (!filter.subtasks.level0) {
        return false
      }
      if (filter.parent === noBoardContainerId) {
        return (
          task.parentTaskIds.length === 0 &&
          task.columnId === noBoardContainerId
        )
      }
      return (
        task.columnId === filter.parent ||
        task.boardId === filter.parent ||
        task.projectId === filter.parent
      )
    }
    result = result.filter(t => located.filter(i => check(t, i)).length)
  }

  if (createdBy && createdBy.length > 0) {
    const check = userChecker(createdBy)
    result = result.filter(t => check(t.createdBy))
  }

  if (assignedTo && assignedTo.length > 0) {
    const check = multiUserChecker(assignedTo)
    result = result.filter(t => check(t.assigned))
  }

  if (responsible && responsible.length > 0) {
    const check = userChecker(responsible)
    result = result.filter(t => check(t.assigned[0]))
  }

  if (assignedBy && assignedBy.length > 0) {
    const check = multiUserChecker(assignedBy)
    result = result.filter(t => check(t.assignedBy))
  }

  if (createdAt) {
    const check = dateChecker(createdAt)
    result = result.filter(t => check(t.createTime))
  }

  if (movedAt) {
    const check = dateChecker(movedAt)
    result = result.filter(t => {
      const maxMoveTime = (t.moveTimes ?? []).reduce(
        (acc, t) => Math.max(acc, t),
        0
      )
      return check(maxMoveTime)
    })
  }

  if (lastMessageAt) {
    const check = dateChecker(lastMessageAt)
    result = result.filter(t => check(t.lastMessageTime))
  }

  if (deadline) {
    const check = dateChecker(deadline)
    result = result.filter(t => {
      if (isSetSticker(t.deadline)) {
        if (deadline.type === 'set') {
          return true
        }
        return check(t.deadline)
      } else {
        return deadline.type === 'not-set'
      }
    })
  }

  if (isArchived && isArchived.type === 'value') {
    result = result.filter(t => !!t.archived === !!isArchived.value)
  }

  if (isCompleted && isCompleted.type === 'value' && !isCompleted.value) {
    result = result.filter(t => t.gtdStage !== 'done')
  } else if (completedAt) {
    const check = dateChecker(completedAt)
    result = result.filter(t => {
      return check(t.completeTime)
    })
  } else if (isCompleted && isCompleted.type === 'value' && isCompleted.value) {
    result = result.filter(t => t.gtdStage === 'done')
  }

  const getSubtasks = (t: TaskData): Subtask[] => {
    if (!t?.subtasks?.subtasks) {
      return []
    }
    const res: Subtask[] = []
    for (const id of Object.keys(t.subtasks.subtasks)) {
      const s = t.subtasks.subtasks[id]
      if (!!s && !taskDataService.isDeleted(s.id)) {
        res.push(s)
      }
    }
    return res
  }

  if (subtasks && subtasks.type === 'value') {
    result = result.filter(t => !!getSubtasks(t).length === !!subtasks.value)
  }

  const hasSubtaskFilter =
    (subtasksCompleted && subtasksCompleted.type === 'value') ||
    (subtasksAssigned && subtasksAssigned.length > 0) ||
    subtasksDeadline
  if (hasSubtaskFilter) {
    result = result.filter(t => !!getSubtasks(t).length)
  }

  if (subtasksCompleted && subtasksCompleted.type === 'value') {
    result = result.filter(t => {
      const sub = getSubtasks(t)
      return (
        sub.length === 0 ||
        sub.every(t => t.isCompleted) === !!subtasksCompleted.value
      )
    })
  }

  const getDeadlines = (t: TaskData): (DeadlineStickerEntry | undefined)[] => {
    const sub = getSubtasks(t)
    return sub.map(
      s =>
        (s.stickers?.[stickerIds.Deadline] as DeadlineStickerValue | undefined)
          ?.current
    )
  }

  if (subtasksDeadline) {
    const check = dateChecker(subtasksDeadline)
    const checkBetween = dateCheckerBetween(subtasksDeadline)

    if (subtasksDeadline.type === 'expired') {
      result = result.filter(t =>
        getDeadlines(t).some(d => d?.deadline && check(d.deadline))
      )
    } else if (subtasksDeadline.type === 'not-expired') {
      result = result.filter(t => {
        const deadlines = getDeadlines(t)
        return (
          deadlines.some(Boolean) &&
          !deadlines.some(d => d?.deadline && !check(d.deadline))
        )
      })
    } else if (subtasksDeadline.type === 'set') {
      result = result.filter(t => !getDeadlines(t).some(d => !d?.deadline))
    } else if (subtasksDeadline.type === 'not-set') {
      result = result.filter(t => getDeadlines(t).some(d => !d?.deadline))
    } else {
      result = result.filter(t => {
        const deadlines = ArrUtil.defined(getDeadlines(t))
        return deadlines.some(d => {
          if (!d.startDate) {
            return d.deadline && check(d.deadline)
          }

          return d.deadline && checkBetween(d.startDate, d.deadline)
        })
      })
    }
  }

  if (subtasksAssigned && subtasksAssigned.length > 0) {
    const check = multiUserChecker(subtasksAssigned)
    result = result.filter(t => {
      const sub = getSubtasks(t)
      const combined: DataObjectId[] = []
      for (const s of sub) {
        const assigned = assignedStickerStateToIds(
          s.stickers?.[stickerIds.User]
        )
        combined.push(...assigned)
      }
      return check(combined)
    })
  }

  if (stickers) {
    for (const stickerId of Object.keys(stickers)) {
      const st = stickers[stickerId]
      result = result.filter(t => {
        if (!t.stickers) {
          return false
        }
        const stateId = t.stickers[stickerId]
        if (stateId === '' && st.values.some(x => x.id === 'not-set')) {
          return true
        }
        if (!!stateId && st.values.some(x => x.id === 'all-filled')) {
          return true
        }
        if (!!stateId && st.values.some(x => x.id === 'set-range')) {
          const values: string[] = st.values.map(value => value.id)
          const from = getRangeValue(values, 'from-')
          const to = getRangeValue(values, 'to-')
          const stValue = Number(stateId)

          if (from === undefined && to === undefined) {
            return true
          }

          if (from !== undefined && to !== undefined) {
            return stValue <= to && stValue >= from
          }

          if (from !== undefined) {
            return stValue >= from
          }

          if (to !== undefined) {
            return stValue <= to
          }
        }
        if (
          stateId === undefined &&
          st.values.some(x => x.id === 'not-pinned')
        ) {
          return true
        }
        if (!st.values || st.values.filter(v => !!v && !!v.id).length === 0) {
          return !!stateId
        } else {
          return (
            !!stateId &&
            (st.values.find(v => v.id === stateId && !v.exclude) ||
              !st.values.find(v => !v.exclude)) &&
            st.values.every(v => !v.exclude || v.id !== stateId)
          )
        }
      })
    }
  }

  if (timetracking?.plan) {
    result = result.filter(d => {
      if (timetracking.plan === 'not-set') {
        return (
          d.timetracking === 'not-set' ||
          (isSetSticker(d.timetracking) && !d.timetracking.plan)
        )
      } else if (timetracking.plan === 'set') {
        return isSetSticker(d.timetracking) && d.timetracking.plan
      } else {
        return false
      }
    })
  }

  if (timetracking?.work) {
    result = result.filter(d => {
      if (timetracking.work === 'not-set') {
        return (
          d.timetracking === 'not-set' ||
          (isSetSticker(d.timetracking) && !d.timetracking.work)
        )
      } else if (timetracking.work === 'set') {
        return isSetSticker(d.timetracking) && d.timetracking.work
      } else if (timetracking.work === '<=plan') {
        return (
          isSetSticker(d.timetracking) &&
          d.timetracking.work &&
          d.timetracking.plan &&
          d.timetracking.work <= d.timetracking.plan
        )
      } else if (timetracking.work === '>plan') {
        return (
          isSetSticker(d.timetracking) &&
          d.timetracking.work &&
          d.timetracking.plan &&
          d.timetracking.work > d.timetracking.plan
        )
      } else {
        return false
      }
    })
  }

  if (timer?.type) {
    result = result.filter(d => {
      if (isSetSticker(d.timer)) {
        if (timer.type === 'set') {
          return d.timer.running
        }
        if (timer.type === 'stopped') {
          return !d.timer.running
        }
        if (timer.type === 'expired' || timer.type === 'not-expired') {
          const interval = timerToInterval(d.timer, true)
          const notExpired = interval && interval.toSeconds() > 0
          return notExpired === (timer.type === 'not-expired')
        }
        return false
      } else {
        return d.timer === timer.type
      }
    })
  }

  if (stopwatch?.type) {
    result = result.filter(d => {
      if (isSetSticker(d.stopwatch)) {
        if (stopwatch.type === 'pinned') {
          return true
        }
        if (stopwatch.type === 'set') {
          return d.stopwatch.running
        }
        if (stopwatch.type === 'stopped') {
          return !d.stopwatch.running
        }
        return false
      } else {
        return d.stopwatch === stopwatch.type
      }
    })
  }

  if (text?.text) {
    result = result.filter(d => {
      const texts: Record<ReportTextFilterPlace, () => string> = {
        'task-title': () => d.title,
        'column-title': () => d.visibleColumnTitle,
        'board-title': () => d.visibleBoardTitle,
        'project-title': () => d.visibleProjectTitle,
        'task-description': () => taskDataService.getDescription(d.id),
        checklists: () =>
          getChecklistsAsText(taskDataService.getTask(d.id)?.data?.checklists),
        filenames: () => taskDataService.getFilenames(d.id),
        messages: () => taskDataService.getMessagesAllText(d.id)
      }
      const allText = text.places.map(x => texts[x]?.() ?? '').join(' ')

      return SearchUtil.smartSearch(allText, text.text)
    })
  }

  if (chat) {
    result = result.filter(d => {
      if (chat.type === 'no-messages') {
        return d.messagesCount === 0
      }
      if (chat.type === 'less') {
        return chat.count && d.messagesCount < chat.count
      }
      if (chat.type === 'more') {
        return chat.count && d.messagesCount > chat.count
      }
      return false
    })
  }

  const allFields = getAllFields(
    getStickersFromTasks(result, allStickers, column.fields)
  )
  const sort: {field: ReportDataField; asc: boolean}[] = []
  for (const s of column.sort ?? []) {
    const field = allFields.find(f => f(Lang.msg).key === s.key)
    if (field) {
      sort.push({field, asc: s.asc})
    }
  }
  const noCreationSort = !sort.find(s => s.field(Lang.msg).key === 'createdAt')
  if (noCreationSort) {
    sort.unshift({field: createdAtField, asc: false})
  }
  sort.forEach(s =>
    result.sort((a, b) => s.field(Lang.msg).order(a, b, !s.asc))
  )

  return result
}
